// Pure, framework-free logic for SubscriptionItem's "Paid Locks (Coming Soon)"
// tier-card bullet. Kept in its own module so it can be unit-tested in a node
// Jest environment without pulling in styled-components / MUI / SVG asset imports.
//
// WL-042 / ISP-9999: when Paid Locks is actually LIVE, the ProSuite/Premium
// "Paid Locks (Coming Soon)" bullet becomes a shipped feature and the
// "(Coming Soon)" qualifier is dropped at render time.
//
// ISP-9999 (fix-editwheel-subscription-copy-paid-locks-coming-soon,
// 2026-09-06): the previous "server-gate" design required BOTH the client
// PAID_LOCKS_ENABLED flag AND a `GET /api/v2/wheel/0/access/` probe reporting
// `feature_enabled:true`. That probe is STRUCTURALLY INCAPABLE of ever
// succeeding: wheel id 0 never exists (Postgres autoincrement starts at 1),
// so once the backend flag is genuinely on, the lookup 400s "Wheel not found"
// instead of returning `feature_enabled`. usePaymentLocking.js's
// `useWheelPaywallAccess` also disables its own query entirely for a falsy
// wheelId (`enabled: !!wheelId && ...`), so the read never even fires. Net
// effect: `paidLocksLive` was permanently `false` in every environment,
// forever — confirmed live on web-dev (bundle main.99ef90b9.js) on 2026-09-06,
// where the Premium/ProSuite cards still read "Paid Locks (Coming Soon)"
// despite Paid Locks being demonstrably live in production (a completed
// Stripe TEST purchase, cs_test_a189sV9KZ..., moved buyer uid 12678 from
// view_only_teaser to unlocked_view) and despite PR #5039 already fixing the
// static TIER_FEATURES fallback copy (the server-driven `feature_splits` copy
// an admin authored still carries the stale qualifier, and nothing was ever
// able to strip it at render time).
//
// PR #5039's own test plan independently confirmed backend liveness the
// correct way — probing a REAL wheel id (`GET /api/v2/wheel/5103/access/` ->
// `feature_enabled:true`), not the impossible id-0 sentinel. The
// PAID_LOCKS_ENABLED build flag (`REACT_APP_PAID_LOCKS`) is deploy-time,
// per-environment, and ops-controlled specifically to mirror backend
// readiness (see constants/featureFlags.js) — it is flipped true only once
// the paired backend `PAYMENT_LOCKING_ENABLED` setting is confirmed on for
// that environment. With no wheel-agnostic "is Paid Locks live" endpoint on
// the backend (still true as of this fix), that flag is the only signal that
// can ever resolve, so it is now trusted directly instead of gating on a
// probe that can mathematically never return true.
export function resolvePaidLocksLive({ clientFlagEnabled } = {}) {
  return clientFlagEnabled === true;
}

/**
 * Resolve the call-to-action label on a subscription plan card.
 *
 * EXTRACTED (in-app-marketing assertion pass, 2026-08-01) from an inline
 * `getButtonText()` closure inside SubscriptionItem.jsx. Behaviour-preserving:
 * the branch order and the strings are byte-identical to the closure it
 * replaces. It moved here so the tier ladder the QA pass DROVE on staging —
 * Free is offered "Subscribe to …" for four plans, Essential "Upgrade to …"
 * for three, ProSuite "Upgrade to Business" only — can be re-proved by a
 * script instead of by an agent clicking through three logins.
 *
 * THE RULE, in precedence order:
 *  1. The plan the user is ON shows the billing-cycle switch, never a
 *     self-sell. This is the guarantee that "no tier is ever sold something it
 *     already owns"; it is checked FIRST so it can never be overridden.
 *  2. The Free plan (sub_tier 0) is a downgrade destination, not a purchase.
 *  3. Everything else title-cases the server's `action` verb ("SUBSCRIBE" /
 *     "UPGRADE") against the plan name. The verb is the SERVER's call — the
 *     client must not invent it, because only the server knows the ladder.
 *
 * @param {object} args
 * @param {boolean} args.isActive     this card is the user's current plan
 *                                    (`is_active` OR `action === "ACTIVE"`)
 * @param {boolean} args.isAnnually   the Annual billing cycle is selected
 * @param {number}  args.subTier      the plan's tier (0 = Free)
 * @param {string}  args.action       the server's verb, e.g. "SUBSCRIBE"
 * @param {string}  args.name         the plan's display name
 * @returns {string} the button label
 */
export function resolveSubscriptionCtaLabel({
  isActive,
  isAnnually,
  subTier,
  action,
  name,
} = {}) {
  // billing-monetization:S2.3 — the held-tier card must read "Current Plan",
  // never a cross-cycle switch. `useSubscription` treats `action==="ACTIVE"`
  // as a no-op (the button is already non-clickable for the active card, see
  // SubscriptionItem.jsx `onClick={!activeItem && onActionPress}`), so the
  // OLD `Switch to ${isAnnually ? "Monthly" : "Annual"}` label pointed at a
  // dead action AND was cross-cycle-inverted (Annual tab showed "Switch to
  // Monthly" and vice versa) — a paying user never saw a "Current" indicator.
  // Same-tier cross-duration switching is a separate, not-yet-built feature
  // (needs BE action semantics); this card is intentionally non-actionable.
  if (isActive) return "Current Plan";
  if (subTier === 0) return "Downgrade to Free";
  return `${
    action?.charAt(0).toUpperCase() + action?.toLowerCase().slice(1)
  } to ${name}`;
}

/**
 * Return the feature-description HTML to render for a plan bullet.
 *
 * Only when `paidLocksLive` is true AND the bullet is the Paid Locks bullet is
 * the "(Coming Soon)" marker stripped (matching both a `<b>(Coming Soon)</b>`
 * wrapper and a bare "(Coming Soon)", covering the static TIER_FEATURES copy and
 * the server-driven `feature_splits` copy). In every other case the description
 * is returned byte-for-byte unchanged — so the flag-OFF / server-not-live path is
 * byte-identical to today's "Paid Locks (Coming Soon)" and there is no false
 * promise.
 *
 * @param {string} desc raw HTML from resolveCardFeatures / TIER_FEATURES
 * @param {boolean} paidLocksLive server-confirmed live signal (see resolvePaidLocksLive)
 * @returns {string}
 */
export function displayFeatureDescription(desc, paidLocksLive) {
  if (!paidLocksLive || typeof desc !== "string") return desc;
  if (desc.includes("Paid Locks") && /coming soon/i.test(desc)) {
    return desc
      .replace(/\s*<b>\s*\(Coming Soon\)\s*<\/b>/i, "")
      .replace(/\s*\(Coming Soon\)/i, "")
      .trim();
  }
  return desc;
}
